// /src/pages/Reforestation.jsx
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Trees, AlertTriangle, MapPin, Users, Target, Calendar, Plus } from 'lucide-react'
import MapComponent from '../components/MapComponent'

const Reforestation = () => {
  const location = useLocation()
  const [projects, setProjects] = useState([
    {
      id: 1,
      name: 'Amazon Rainforest Restoration',
      location: 'Brazil, South America',
      status: 'Active',
      treesPlanted: 15000,
      target: 50000,
      participants: 250,
      startDate: '2024-01-15',
      description: 'Large-scale reforestation project in the Amazon basin focusing on native species restoration.',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop',
      coordinates: { lat: -3.4653, lng: -58.3804 }
    },
    {
      id: 2,
      name: 'Urban Forest Initiative',
      location: 'New York, USA',
      status: 'Planning',
      treesPlanted: 0,
      target: 10000,
      participants: 45,
      startDate: '2024-03-01',
      description: 'Creating urban forests in New York City to improve air quality and provide green spaces.',
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop',
      coordinates: { lat: 40.7128, lng: -74.0060 }
    }
  ])
  
  // Scroll to top when location changes (route navigation)
  useEffect(() => {
    // Multiple aggressive approaches to ensure scroll to top
    const scrollToTop = () => {
      // Method 1: Immediate scroll
      window.scrollTo(0, 0)
      
      // Method 2: Document element scroll
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
      
      // Method 3: Force scroll after a brief delay
      setTimeout(() => {
        window.scrollTo(0, 0)
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }, 50)
      
      // Method 4: Smooth scroll as final attempt
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }, 100)
    }
    
    // Execute immediately
    scrollToTop()
    
    // Also execute after a longer delay to catch any late overrides
    setTimeout(scrollToTop, 200)
    setTimeout(scrollToTop, 500)
  }, [location.pathname])

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700'
      case 'planning':
        return 'bg-yellow-100 text-yellow-700'
      case 'completed':
        return 'bg-blue-100 text-blue-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  const totalTreesPlanted = projects.reduce((sum, project) => sum + project.treesPlanted, 0)
  const totalTarget = projects.reduce((sum, project) => sum + project.target, 0)
  const totalParticipants = projects.reduce((sum, project) => sum + project.participants, 0)

  return (
    <div className="min-h-screen bg-forest-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-forest-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <Trees className="w-16 h-16 text-primary-600 mx-auto mb-4" />
            <h1 className="text-4xl font-bold text-forest-900 mb-2">Reforestation Projects</h1>
            <p className="text-xl text-forest-600">
              Join global reforestation efforts and track our impact on the planet
            </p>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
            <div className="flex items-center">
              <Trees className="w-8 h-8 text-primary-600 mr-4" />
              <div>
                <p className="text-sm text-forest-600">Total Trees Planted</p>
                <p className="text-2xl font-bold text-forest-900">{totalTreesPlanted.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
            <div className="flex items-center">
              <Target className="w-8 h-8 text-primary-600 mr-4" />
              <div>
                <p className="text-sm text-forest-600">Target Goal</p>
                <p className="text-2xl font-bold text-forest-900">{totalTarget.toLocaleString()}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-primary-600 mr-4" />
              <div>
                <p className="text-sm text-forest-600">Participants</p>
                <p className="text-2xl font-bold text-forest-900">{totalParticipants.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-forest-900 mb-4">Project Locations</h2>
          <MapComponent
            onCoordinatesChange={() => {}}
            height="400px"
            showSearch={true}
            projects={projects}
          />
        </div>

        {/* Projects List */}
        <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-forest-900">Active Projects</h2>
            <button className="btn-primary inline-flex items-center space-x-2">
              <Plus className="w-4 h-4" />
              <span>Add Project</span>
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {projects.map((project) => (
              <div key={project.id} className="border border-forest-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-forest-900 mb-1">{project.name}</h3>
                    <div className="flex items-center text-sm text-forest-600 mb-2">
                      <MapPin className="w-4 h-4 mr-1" />
                      {project.location}
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                    {project.status}
                  </span>
                </div>
                
                <p className="text-forest-700 mb-4 line-clamp-2">{project.description}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-forest-600">Trees Planted</p>
                    <p className="text-lg font-semibold text-forest-900">{project.treesPlanted.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-forest-600">Target</p>
                    <p className="text-lg font-semibold text-forest-900">{project.target.toLocaleString()}</p>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm text-forest-600">
                    <Users className="w-4 h-4 mr-1" />
                    {project.participants} participants
                  </div>
                  <div className="flex items-center text-sm text-forest-600">
                    <Calendar className="w-4 h-4 mr-1" />
                    Started {new Date(project.startDate).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Reforestation 